"use client";
import { useEffect, useRef } from "react";
import SVG from "react-inlinesvg";
import NewsList from "@/components/utils/NewsList";
import AccordionLarge from "@/components/utils/AccordionLarge";
import AnchorLink from "@/components/utils/AnchorLink";
import InViewAnimation from "@/components/utils/InViewAnimation";
import Link from "next/link";
import { useInView } from "react-intersection-observer";

export default function PageComponent({posts}) {
  return (
    <main className="page page-companies">
      <div className="container-m">
        <InViewAnimation fadeOnly={true}>
          <section className="section section-kv">
            <div className="kv-pic pcOnly">
              <img src="/assets/img/for-companies/kv-pic.jpg" alt="" />
            </div>
            <div className="kv-title-holder">
              <div className="shoulder">
                <span className="en small">For Companies</span>
                <span className="bold">企業のみなさまへ</span>
              </div>
              <h1 className="h1-title bold">
                農業を通じて、<br />
                企業のさまざまな課題を<br />
                解決します。
              </h1>
              <div className="kv-pic spOnly">
                <img src="/assets/img/for-companies/kv-pic.jpg" alt="" />
              </div>
              <div className="anchor-list">
                <AnchorLink href="#section-1" className="anchor-list-item scaleHover">
                  <span>
                    <i className="icon">
                      <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                    </i>
                    BGの企業向けソリューション
                  </span>
                </AnchorLink>
                <AnchorLink href="#section-2" className="anchor-list-item scaleHover">
                  <span>
                    <i className="icon">
                      <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                    </i>
                    ケーススタディ
                  </span>
                </AnchorLink>
              </div>
            </div>
          </section>

          <div className="separator-asterisk en">*</div>

          <section className="section section-1" id="section-1">
            <div className="section-title-holder">
              <h2 className="section-title bold">
                BGの企業向けソリューション
              </h2>
              <div className="section-lead bold">
                農業は、環境に配慮した取り組みが農産物のおいしさにもつながる、環境貢献と経済成長の両立を実現する可能性を秘めた産業です。BGは、この農業の力を活かし、企業の目指す姿や抱える課題に対して、多様なソリューションメニューを提供し、事業の付加価値向上をご支援します。
              </div>
            </div>

            <div className="block">
              <picture className="pic n1">
                <source srcSet="/assets/img/for-companies/section-1-pic.png" media="(min-width: 1024px)" />
                <source srcSet="/assets/img/for-companies/section-1-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/for-companies/section-1-pic.png" alt="" loading="lazy" />
              </picture>

              <div className="method-list-block">
                <div className="method-list-title">
                  ソリューションメニュー
                </div>
                <div className="method-list-title en">
                  Next Green Method
                </div>
                <div className="method-list">
                  <div className="method-list-item">
                    <div className="title bold">農業支援</div>
                    <div className="text bold">（Soil Solution）</div>
                    <div className="text bold">土づくりによる次世代の農業への転換支援</div>
                  </div>
                  <div className="method-list-item">
                    <div className="title bold">事業ブランディング</div>
                    <div className="text bold">（Next Green Credit）</div>
                    <div className="text bold">環境クレジットの活用による農業支援・環境貢献</div>
                  </div>
                  <div className="method-list-item">
                    <div className="title bold">環境影響可視化</div>
                    <div className="text bold">（Agri LCA+）</div>
                    <div className="text bold">事業の環境影響の可視化</div>
                  </div>
                  <div className="method-list-item">
                    <div className="title bold">資源循環</div>
                    <div className="text bold">（Soil Next）</div>
                    <div className="text bold">地域の有機廃棄物の資源化・農業活用</div>
                  </div>
                </div>
              </div>

              <div className="method-list-block pink">
                <div className="method-list-title en">
                  Next Green Vegetables
                </div>
                <div className="method-list">
                  <div className="method-list-item">
                    <div className="title bold">野菜利用</div>
                    <div className="text bold">付加価値野菜の利用</div>
                  </div>
                  <div className="method-list-item">
                    <div className="title bold">CSA</div>
                    <div className="text bold">生活者が農業に関わる体験</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </InViewAnimation>

        <div className="separator-asterisk en">*</div>

        <section className="section section-2" id="section-2">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              ケーススタディ
            </h2>
          </div>

          <div className="block">
            <div className="accordion-contianer-wrap">
              <AccordionLarge>
                <div className="case-study-kv">
                  <div className="company-logo n1">
                    <img src="/assets/img/for-companies/company-logo-1.svg" alt="YANMAR"/>
                  </div>
                  <div className="case-study-title">
                    <span className="small">農業機械メーカー ヤンマーホールディングス株式会社<br className="spOnly" />（ヤンマーeスター株式会社）様との連携</span>
                    <span className="bold">環境影響可視化×資源循環</span>
                  </div>
                  <div className="case-study-lead">
                    食品廃棄物を分解し資源循環サイクルを実現するバイオ式コンポスター「YC100」の訴求力の向上のため、本機導入による地球環境への好影響を「Agri LCA+」により可視化・証明。
                  </div>
                </div>
                <div className="accordion-content">
                  <div className="accordion-content-inner">
                    <ul className="line-list">
                      <li className="line-list-item">
                        <p className="item-title">課題</p>
                        <ul>
                          <li>
                            YC100のベネフィットの訴求力向上
                          </li>
                        </ul>
                      </li>
                      <li className="line-list-item">
                        <p className="item-title">ソリューション</p>
                        <ul>
                          <li>
                            Agri LCA+によるコンポスターの資源循環の環境影響の可視化
                          </li>
                        </ul>
                      </li>
                    </ul>

                    <div className="block-ambition round">
                      <div className="title-lead">
                        ヤンマーホールディングス株式会社<br />
                        技術本部　イノベーションセンター　<br className="spOnly" />プロトタイプ開発部<br />
                        コンポスタグループ　グループリーダー
                      </div>
                      <div className="title bold">中山　法和様</div>
                      <div className="description">
                        これまで、環境貢献を謳うバイオ式コンポスターによる資源循環が、地球環境とのトレードオフ（コンポストに使用する電気など）を生んでいないか客観的な可視化ができていませんでした。BGの「Agri LCA+」により、コンポスター導入による環境影響の変化を可視化したことで、顧客に対してコンポスターの導入価値を定量的に示すことができるようになりました。ヤンマーは農業に根ざす会社として、食品廃棄物の有効活用による資源循環によって、持続可能な農業を目指しています。今後ヤンマーは、BG社と連携し、「YC100」で処理後の生成物のより高品質な堆肥化に取り組むなど、資源循環に貢献するソリューション提案を強化します。
                      </div>
                    </div>

                    <picture className="pic round n1">
                      <img src="/assets/img/for-companies/case-study-1-1.jpg" alt="" loading="lazy" />
                    </picture>
                    <picture className="pic n2">
                      <div className="company-logo n1">
                        <img src="/assets/img/for-companies/company-logo-1.svg" alt="YANMAR"/>
                      </div>
                      <source srcSet="/assets/img/for-companies/case-study-1-2.png" media="(min-width: 1024px)" />
                      <source srcSet="/assets/img/for-companies/case-study-1-2-sp.png" media="(max-width: 1024px)" />
                      <img src="/assets/img/for-companies/case-study-1-2.png" alt="" loading="lazy" />
                    </picture>

                    <ScrollContaienr>
                      <picture className="tab-item-pic">
                        <source srcSet="/assets/img/for-companies/case-study-1-3.png" media="(min-width: 1024px)" />
                        <source srcSet="/assets/img/for-companies/case-study-1-3-sp.png" media="(max-width: 1024px)" />
                        <img src="/assets/img/for-companies/case-study-1-3.png" alt="" loading="lazy" />
                      </picture>
                    </ScrollContaienr>
                  </div>
                </div>
              </AccordionLarge>
              <AccordionLarge>
                <div className="case-study-kv">
                  <div className="company-logo n2">
                    <img src="/assets/img/for-companies/company-logo-2.svg" alt="東京建物"/>
                  </div>
                  <div className="case-study-title">
                    <span className="small">デベロッパー 東京建物株式会社様との協業</span>
                    <span className="bold">クレジット活用×野菜利用</span>
                  </div>
                  <div className="case-study-lead">
                    BGは、2026年2月に竣工予定の東京駅直結の大規模複合施設「TOFROM YAESU TOWER」において、同年秋にオープン予定の共用フロア「Wab.」内の共用社員食堂を環境クレジット「Next Green Credit」によりカーボンニュートラル化することを支援。また、おいしい理由が可視化された野菜ブランド「Next Green Vegetables」を食材として提供し、つくり手の取組みや想いを日常的に伝えることで、オフィスワーカーがおいしい体験や学びを得ながら、その一食一食が農業支援や環境貢献につながる、人と地球に健康的な、これまでにない新しい共用社員食堂の開発に向けて連携。
                  </div>
                </div>
                <div className="accordion-content">
                  <div className="accordion-content-inner">
                    <ul className="line-list">
                      <li className="line-list-item">
                        <p className="item-title">課題</p>
                        <ul>
                          <li>
                            共用社員食堂の差別化
                          </li>
                          <li>
                            オフィスワーカーのウェルビーイング向上
                          </li>
                        </ul>
                      </li>
                      <li className="line-list-item">
                        <p className="item-title">ソリューション</p>
                        <ul>
                          <li>
                            Next Green Creditの購入による共用社員食堂のカーボンニュートラル化
                          </li>
                          <li>
                            Next Green Vegetablesの食材利用による共用社員食堂の付加価値向上
                          </li>
                        </ul>
                      </li>
                    </ul>

                    <div className="block-ambition round">
                      <div className="title-lead">
                        都市開発事業第一部　<br className="spOnly" />八重洲一丁目東プロジェクト推進室　室長　兼<br />
                        まちづくり推進部 FOOD&イノベーション推進室　課長
                      </div>
                      <div className="title bold">沢　俊和様</div>
                      <div className="description">
                        東京建物は、東京からリジェネラティブな世界を目指す「Regenerative City Tokyo」構想の一つとして、次世代農業への革命「Next Green Revolution」に参画しました。東京駅前の大規模複合施設「TOFROM YAESU」を起点に、双方向の価値・人の循環を生む仕組みを通じて、都市と地方が共に「再生」し合う新たなエコシステムの構築を目指します。おいしい理由が可視化された野菜ブランド「Next Green Vegetables」を同施設内の共用社員食堂で提供することや農家とオフィスワーカーの交流機会を作ることで、オフィスワーカーが日常的に“おいしい”と“地球にやさしい”を体験できる場を創出。さらに環境クレジット「Next Green Credit」で食堂をカーボンニュートラル化します。これらの取り組みを通じて、東京建物は「Regenerative City Tokyo」構想とワーカーのウェルビーイング向上を実現します。
                      </div>
                    </div>

                    <picture className="pic round n1">
                      <img src="/assets/img/for-companies/case-study-2-1.jpg" alt="" loading="lazy" />
                    </picture>

                    <picture className="pic n2">
                      <div className="company-logo n2">
                        <img src="/assets/img/for-companies/company-logo-2.svg" alt="YANMAR"/>
                      </div>
                      <source srcSet="/assets/img/for-companies/case-study-2-2.png" media="(min-width: 1024px)" />
                      <source srcSet="/assets/img/for-companies/case-study-2-2-sp.png" media="(max-width: 1024px)" />
                      <img src="/assets/img/for-companies/case-study-2-2.png" alt="" loading="lazy" />
                    </picture>

                    <ScrollContaienr>
                      <picture className="tab-item-pic">
                        <source srcSet="/assets/img/for-companies/case-study-2-3.png" media="(min-width: 1024px)" />
                        <source srcSet="/assets/img/for-companies/case-study-2-3-sp.png" media="(max-width: 1024px)" />
                        <img src="/assets/img/for-companies/case-study-2-3.png" alt="" loading="lazy" />
                      </picture>
                    </ScrollContaienr>
                  </div>
                </div>
              </AccordionLarge>
              <AccordionLarge>
                <div className="case-study-kv">
                  <div className="company-logo n3">
                    <img src="/assets/img/for-companies/company-logo-3.svg" alt="日鉄興和不動産"/>
                  </div>
                  <div className="case-study-title">
                    <span className="small">デベロッパー 日鉄興和不動産株式会社様との協業</span>
                    <span className="bold">資源循環×クレジット創出・活用</span>
                  </div>
                  <div className="case-study-lead">
                    マンション開発敷地に自生する既存樹木の再利用を通じた持続可能な社会への取り組み「緑の循環プロジェクト」において、BGの有機発酵資材「Soil Next」の知見を活かして保存・移植に適さない既存樹木を高品質な農業資材として再資源化し、農業に活用。その資材が投入された畑から創出される環境クレジット「Next Green Credit」を購入することで、マンション開発に伴う環境負荷の一部を企業として責任を持って還元する仕組みを構築。また、その畑とマンションの入居者をつなぐ「CSA」（地域支援型農業）の仕組みによって、おいしい食との出会いや、農家との触れ合いを通じて農業の価値を感じる新たな体験を創出。
                  </div>
                </div>
                <div className="accordion-content">
                  <div className="accordion-content-inner">
                    <ul className="line-list">
                      <li className="line-list-item">
                        <p className="item-title">課題</p>
                        <ul>
                          <li>
                            マンション開発の過程における既存樹木の伐採による環境負荷
                          </li>
                        </ul>
                      </li>
                      <li className="line-list-item">
                        <p className="item-title">ソリューション</p>
                        <ul>
                          <li>
                            Soil Nextの知見に基づく既存樹木の農業資材としての再資源化
                          </li>
                          <li>
                            Next Green Creditの購入によるマンション開発に伴う環境負荷に対する責任の遂行
                          </li>
                          <li>
                            CSAによるマンション入居者への農業の価値体験の提供
                          </li>
                        </ul>
                      </li>
                    </ul>

                    <div className="block-ambition round">
                      <div className="title-lead">
                        住宅事業本部 開発推進部 <br className="spOnly" />商品企画グループ グループリーダー 兼 <br />
                        住宅事業本部 開発推進部 <br className="spOnly" />開発推進第一グループ 統活マネージャー 兼 <br />
                        企画本部 経営企画部 <br className="spOnly" />サステナビリティ推進室 統括マネージャー
                      </div>
                      <div className="title bold">吉野　奈美子様</div>
                      <div className="description">
                        今回のBG社との連携は、資源循環の実現に留まらず、クレジットの購入によって、マンション開発に伴う環境負荷の一部を農業支援にもつながるものとして還元し、さらにこうした農業との接点が、CSAの仕組みを通じて農業体験やマルシェの開催など、マンションに住まう方々の人生をより豊かにすることにもつながる、社会的にも事業的にも意義のある「緑の価値の循環」を生む取り組みと考えています。<br />
                        まずは東京都練馬区の「リビオ光が丘ガーデンズ」からスタートし、今後は住宅事業に限らず当社が手がけるさまざまな開発においても導入を進め、農業領域における緑の循環を広げていきます。
                      </div>
                    </div>

                    <picture className="pic round n1">
                      <img src="/assets/img/for-companies/case-study-3-1.jpg" alt="" loading="lazy" />
                    </picture>

                    <picture className="pic n2">
                      <div className="company-logo n3">
                        <img src="/assets/img/for-companies/company-logo-3.svg" alt="YANMAR"/>
                      </div>
                      <source srcSet="/assets/img/for-companies/case-study-3-2.png" media="(min-width: 1024px)" />
                      <source srcSet="/assets/img/for-companies/case-study-3-2-sp.png" media="(max-width: 1024px)" />
                      <img src="/assets/img/for-companies/case-study-3-2.png" alt="" loading="lazy" />
                    </picture>
                    
                    <ScrollContaienr>
                      <picture className="tab-item-pic">
                        <source srcSet="/assets/img/for-companies/case-study-3-3.png" media="(min-width: 1024px)" />
                        <source srcSet="/assets/img/for-companies/case-study-3-3-sp.png" media="(max-width: 1024px)" />
                        <img src="/assets/img/for-companies/case-study-3-3.png" alt="" loading="lazy" />
                      </picture>
                    </ScrollContaienr>
                  </div>
                </div>
              </AccordionLarge>
            </div>
          </div>
        </section>
      </div>

      {posts.length > 0 && (
        <>
          <div className="separator-asterisk en">*</div>

          <div className="container-m">
            <section className="section section-method-latestcase">
              <div className="section-title-holder">
                <h2 className="section-title bold">
                  最新事例
                </h2>
              </div>
              <div className="container-xs grayCard">
                <NewsList posts={posts} />
                <Link href="/news/implementation/companies/" className="button-link button-round scaleHover size-s">
                  <span className="inner">すべての事例をみる</span>
                </Link>
              </div>
            </section>
          </div>
        </>
      )}
    </main>
  );
}

const ScrollContaienr = ({children}) => {
  const tabEl = useRef(null);
  const before = (isScroll) => {
    tabEl.current.querySelector(".tab-button.before").classList.add("current")
    tabEl.current.querySelector(".tab-button.after").classList.remove("current")
    if(isScroll) {
      tabEl.current.querySelector(".tab-item").scroll({
        left: 0,
        behavior: "smooth",
      });
    }
  }
  const after = (isScroll) => {
    tabEl.current.querySelector(".tab-button.before").classList.remove("current")
    tabEl.current.querySelector(".tab-button.after").classList.add("current")
    if(isScroll) {
      tabEl.current.querySelector(".tab-item").scroll({
        left: tabEl.current.querySelector(".tab-item-inner").clientWidth - tabEl.current.querySelector(".tab-item").clientWidth,
        behavior: "smooth",
      });
    }
  }
  const { ref, inView, entry } = useInView({
    rootMargin: "10000px -50%",
  });

  useEffect(() => {
    if(tabEl.current) {
      if(inView) {
        before(false)
      } else {
        after(false)
      }
    }
  },[inView])

  return (
    <div className="block-tab" ref={tabEl}>
      <div className="tab-button-list">
        <button className="tab-button en bold current before" type="button" onClick={() => {
          before(true)
        }}>Before</button>
        <button className="tab-button en bold after" type="button" onClick={() => {
          after(true)
        }}>After</button>
      </div>
      <div className="tab-item">
        <div className="tab-item-inner">
          {children}
          <div className="observer" ref={ref}></div>
        </div>
      </div>
    </div>
  )
}
